import { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useDashboardRolloutHealth } from '../../apps/apis/claudeCodeDashboard/hooks';
import type {
  DashboardRolloutHealthEmployee,
  DashboardRolloutHealthSortBy,
  DashboardRolloutMcpStatus,
  DashboardRolloutSetupStatus,
  DashboardRolloutTelemetryStatus,
  DashboardRolloutUpdateStatus,
} from '../../apps/apis/claudeCodeDashboard/types';
import { ROUTES } from '../../routes/routes';
import {
  HELP_TEXT,
  ROLLOUT_MCP_HELP,
  ROLLOUT_SETUP_HELP,
  ROLLOUT_TELEMETRY_HELP,
  ROLLOUT_UPDATE_HELP,
} from '../../components/claude-code/agentTelemetryHelp';
import { HelpBadge, HelpLabel } from '../../components/claude-code/HelpTooltip';
import { ROLE_OPTIONS, roleLabel } from './roleAssetUi';

const PAGE_SIZE = 50;

const MCP_STATUS_OPTIONS: Array<{ value: '' | DashboardRolloutMcpStatus; label: string }> = [
  { value: '', label: 'All MCP statuses' },
  { value: 'configured', label: 'Configured' },
  { value: 'used', label: 'Used' },
  { value: 'missing', label: 'Missing' },
];

const SORT_OPTIONS: Array<{ value: DashboardRolloutHealthSortBy; label: string }> = [
  { value: 'lastSeen', label: 'Last seen' },
  { value: 'pluginVersion', label: 'Plugin version' },
  { value: 'role', label: 'Role' },
  { value: 'stale', label: 'Stale plugin first' },
  { value: 'error', label: 'Errors first' },
];

interface AgentRolloutHealthPanelProps {
  windowDays: number;
}

const formatDate = (value: string | null) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
};

const formatVersion = (value: string | null) => value ?? 'unknown';

const telemetryVariant = (status: DashboardRolloutTelemetryStatus) => {
  switch (status) {
    case 'healthy':
      return 'success';
    case 'quiet':
      return 'warning';
    case 'silent':
      return 'danger';
  }
};

const setupVariant = (status: DashboardRolloutSetupStatus) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
    case 'unknown':
      return 'secondary';
  }
};

const updateVariant = (status: DashboardRolloutUpdateStatus) => {
  switch (status) {
    case 'applied':
      return 'success';
    case 'available':
      return 'warning';
    case 'failed':
      return 'danger';
    case 'checked':
      return 'secondary';
    case 'unknown':
      return 'secondary';
  }
};

const mcpVariant = (status: DashboardRolloutMcpStatus) => {
  switch (status) {
    case 'configured':
      return 'success';
    case 'used':
      return 'info';
    case 'missing':
      return 'secondary';
  }
};

const AgentRolloutHealthPanel = ({ windowDays }: AgentRolloutHealthPanelProps) => {
  const [role, setRole] = useState('');
  const [pluginVersion, setPluginVersion] = useState('');
  const [mcpStatus, setMcpStatus] = useState<'' | DashboardRolloutMcpStatus>('');
  const [sortBy, setSortBy] = useState<DashboardRolloutHealthSortBy>('lastSeen');
  const [offset, setOffset] = useState(0);
  const query = useDashboardRolloutHealth(windowDays, {
    role,
    pluginVersion,
    mcpStatus,
    sortBy,
    limit: PAGE_SIZE,
    offset,
  });

  useEffect(() => {
    setOffset(0);
  }, [windowDays, role, pluginVersion, mcpStatus, sortBy]);

  const data = query.data;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const canGoBack = offset > 0;
  const canGoForward = data ? offset + data.limit < data.total : false;
  const visibleStart = data && data.total > 0 ? offset + 1 : 0;
  const visibleEnd = data ? Math.min(offset + data.limit, data.total) : 0;

  return (
    <Card className="mb-3 border-primary-subtle">
      <Card.Body>
        <div className="d-flex align-items-start justify-content-between gap-2 flex-wrap mb-3">
          <div>
            <Card.Title className="h5 mb-1">
              <HelpLabel description="Install, update, setup, telemetry, and MCP readiness across employees.">
                Plugin rollout health
              </HelpLabel>
            </Card.Title>
            <div className="text-muted small">
              Install, update, setup, and MCP health for the selected telemetry window.
            </div>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
          >
            Refresh
          </Button>
        </div>

        <div className="d-flex gap-2 flex-wrap align-items-end mb-3">
          <Form.Group style={{ minWidth: 220 }}>
            <Form.Label className="small text-muted">
              <HelpLabel description={HELP_TEXT.roles}>Role</HelpLabel>
            </Form.Label>
            <Form.Select value={role} onChange={(event) => setRole(event.target.value)}>
              <option value="">All roles</option>
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group style={{ minWidth: 180 }}>
            <Form.Label className="small text-muted">
              <HelpLabel description={HELP_TEXT.pluginVersion}>Plugin version</HelpLabel>
            </Form.Label>
            <Form.Control
              value={pluginVersion}
              onChange={(event) => setPluginVersion(event.target.value)}
              placeholder="0.5.49 or unknown"
            />
          </Form.Group>
          <Form.Group style={{ minWidth: 190 }}>
            <Form.Label className="small text-muted">
              <HelpLabel description={HELP_TEXT.mcpStatus}>MCP status</HelpLabel>
            </Form.Label>
            <Form.Select
              value={mcpStatus}
              onChange={(event) => setMcpStatus(event.target.value as '' | DashboardRolloutMcpStatus)}
            >
              {MCP_STATUS_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group style={{ minWidth: 190 }}>
            <Form.Label htmlFor="agent-rollout-sort" className="small text-muted">
              <HelpLabel description="Ordering used for the employee rollout table.">Sort</HelpLabel>
            </Form.Label>
            <Form.Select
              id="agent-rollout-sort"
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value as DashboardRolloutHealthSortBy)}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>

        {query.isLoading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" />
          </div>
        ) : query.error ? (
          <Alert variant="danger">Failed to load rollout health: {(query.error as Error).message}</Alert>
        ) : data ? (
          <>
            <Row className="g-2 mb-3">
              <SummaryCard
                title="Installed"
                description={HELP_TEXT.rolloutInstalled}
                value={data.summary.installedEmployees}
                detail={`${data.summary.activeEmployees} active`}
              />
              <SummaryCard
                title="Telemetry healthy"
                description={HELP_TEXT.rolloutHealthy}
                value={data.summary.telemetryHealthyEmployees}
                detail={`${data.summary.noRecentTelemetryEmployees} silent`}
                variant={data.summary.noRecentTelemetryEmployees > 0 ? 'warning' : 'success'}
              />
              <SummaryCard
                title="Latest observed"
                description={HELP_TEXT.latestObserved}
                value={data.summary.latestObservedPluginVersion ?? '-'}
                detail={`${data.summary.latestVersionEmployees} latest / ${data.summary.staleVersionEmployees} stale`}
                variant={data.summary.staleVersionEmployees > 0 ? 'warning' : 'success'}
              />
              <SummaryCard
                title="Setup"
                description={HELP_TEXT.rolloutSetup}
                value={data.summary.setupCompletedEmployees}
                detail={`${data.summary.setupFailedEmployees} failed`}
                variant={data.summary.setupFailedEmployees > 0 ? 'danger' : 'secondary'}
              />
              <SummaryCard
                title="MCP"
                description={HELP_TEXT.rolloutMcp}
                value={data.summary.mcpConfiguredEmployees}
                detail={`${data.summary.mcpUsedEmployees} configured or used`}
              />
              <SummaryCard
                title="Updates"
                description={HELP_TEXT.rolloutUpdates}
                value={data.summary.updateAvailableEmployees}
                detail={`${data.summary.updateFailedEmployees} failed`}
                variant={data.summary.updateFailedEmployees > 0 ? 'danger' : 'secondary'}
              />
            </Row>

            <Row className="g-3">
              <Col xl={4}>
                <VersionAdoptionTable data={data.versionAdoption} />
              </Col>
              <Col xl={8}>
                <EmployeeHealthTable employees={data.employees} />
                <div className="d-flex justify-content-between align-items-center gap-2 mt-2 flex-wrap">
                  <div className="text-muted small">
                    Showing {visibleStart}-{visibleEnd} of {data.total} employees · page {page}
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={!canGoBack}
                      onClick={() => setOffset(Math.max(offset - PAGE_SIZE, 0))}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={!canGoForward}
                      onClick={() => setOffset(offset + PAGE_SIZE)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>

            <div className="text-muted small mt-3">
              Latest is based on the highest displayable plugin version observed in telemetry, not the static release
              manifest.
            </div>
          </>
        ) : null}
      </Card.Body>
    </Card>
  );
};

interface SummaryCardProps {
  title: string;
  description: string;
  value: string | number;
  detail: string;
  variant?: string;
}

function SummaryCard({ title, description, value, detail, variant = 'primary' }: SummaryCardProps) {
  return (
    <Col sm={6} lg={4} xl={2}>
      <Card className="h-100 bg-light border-0">
        <Card.Body className="py-3">
          <div className="text-muted small">
            <HelpLabel description={description}>{title}</HelpLabel>
          </div>
          <div className={`h4 mb-1 text-${variant}`}>{typeof value === 'number' ? value.toLocaleString() : value}</div>
          <div className="text-muted small">{detail}</div>
        </Card.Body>
      </Card>
    </Col>
  );
}

function VersionAdoptionTable({
  data,
}: {
  data: Array<{
    pluginVersion: string | null;
    employeeCount: number;
    activeEmployeeCount: number;
    sessionCount: number;
    lastSeenAt: string | null;
    isLatestObserved: boolean;
  }>;
}) {
  if (data.length === 0) {
    return (
      <Card>
        <Card.Body className="text-muted small">No plugin versions observed.</Card.Body>
      </Card>
    );
  }
  return (
    <Card className="h-100">
      <Card.Header className="fw-semibold">
        <HelpLabel description={HELP_TEXT.versionAdoption}>Version adoption</HelpLabel>
      </Card.Header>
      <Card.Body className="p-0">
        <Table size="sm" responsive className="mb-0 align-middle">
          <thead>
            <tr>
              <th>
                <HelpLabel description={HELP_TEXT.pluginVersion}>Version</HelpLabel>
              </th>
              <th className="text-end">
                <HelpLabel description={HELP_TEXT.employees}>Employees</HelpLabel>
              </th>
              <th className="text-end">
                <HelpLabel description={HELP_TEXT.sessions}>Sessions</HelpLabel>
              </th>
              <th>
                <HelpLabel description={HELP_TEXT.lastSeen}>Last seen</HelpLabel>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.pluginVersion ?? 'unknown'}>
                <td>
                  <HelpBadge
                    bg={row.isLatestObserved ? 'success' : 'secondary'}
                    description={
                      row.isLatestObserved
                        ? 'Latest plugin version observed in telemetry.'
                        : 'Older or unknown plugin version observed in telemetry.'
                    }
                  >
                    {formatVersion(row.pluginVersion)}
                  </HelpBadge>
                  <div className="text-muted small">{row.activeEmployeeCount.toLocaleString()} active</div>
                </td>
                <td className="text-end">{row.employeeCount.toLocaleString()}</td>
                <td className="text-end">{row.sessionCount.toLocaleString()}</td>
                <td className="text-muted small">{formatDate(row.lastSeenAt)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

function EmployeeHealthTable({ employees }: { employees: DashboardRolloutHealthEmployee[] }) {
  if (employees.length === 0) {
    return (
      <Card>
        <Card.Body className="text-muted">No employees match these rollout filters.</Card.Body>
      </Card>
    );
  }
  return (
    <div className="table-responsive">
      <Table size="sm" hover className="mb-0 align-middle">
        <thead>
          <tr>
            <th>
              <HelpLabel description="Employee email or id observed in plugin rollout telemetry.">Employee</HelpLabel>
            </th>
            <th>
              <HelpLabel description={HELP_TEXT.telemetry}>Telemetry</HelpLabel>
            </th>
            <th>
              <HelpLabel description={HELP_TEXT.pluginVersion}>Plugin</HelpLabel>
            </th>
            <th>
              <HelpLabel description={HELP_TEXT.roles}>Roles</HelpLabel>
            </th>
            <th>
              <HelpLabel description={HELP_TEXT.mcpStatus}>MCP</HelpLabel>
            </th>
            <th>
              <HelpLabel description={HELP_TEXT.setup}>Setup</HelpLabel>
            </th>
            <th>
              <HelpLabel description={HELP_TEXT.update}>Update</HelpLabel>
            </th>
            <th className="text-end">
              <HelpLabel description={HELP_TEXT.sessions}>Sessions</HelpLabel>
            </th>
            <th className="text-end">
              <HelpLabel description={HELP_TEXT.events}>Events</HelpLabel>
            </th>
            <th>
              <HelpLabel description={HELP_TEXT.lastSeen}>Last seen</HelpLabel>
            </th>
          </tr>
        </thead>
        <tbody>
          {employees.map((employee) => (
            <EmployeeHealthRow key={employee.employeeId} employee={employee} />
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function EmployeeHealthRow({ employee }: { employee: DashboardRolloutHealthEmployee }) {
  const observedMcpServers = employee.mcpServers ?? [];
  const selectedMcpServers = employee.selectedMcpServers ?? [];

  return (
    <tr>
      <td>
        <Link to={ROUTES.agentTelemetry.employeeDetail(employee.employeeId)}>{employee.employeeId}</Link>
        <div className="text-muted small">first {formatDate(employee.firstSeenAt)}</div>
      </td>
      <td>
        <HelpBadge
          bg={telemetryVariant(employee.telemetryStatus)}
          description={ROLLOUT_TELEMETRY_HELP[employee.telemetryStatus]}
        >
          {employee.telemetryStatus}
        </HelpBadge>
      </td>
      <td>
        <HelpBadge
          bg={employee.isLatestObservedVersion ? 'success' : employee.isStalePlugin ? 'warning' : 'secondary'}
          description={
            employee.isLatestObservedVersion
              ? 'Employee is on the latest observed plugin version.'
              : employee.isStalePlugin
                ? 'Employee appears to be on an older plugin version.'
                : 'Plugin version is unknown or non-semver.'
          }
        >
          {formatVersion(employee.pluginVersion)}
        </HelpBadge>
      </td>
      <td className="text-muted small">{employee.roles.length ? employee.roles.map(roleLabel).join(', ') : '-'}</td>
      <td>
        <div className="d-flex gap-1 flex-wrap align-items-center">
          <HelpBadge bg={mcpVariant(employee.mcpStatus)} description={ROLLOUT_MCP_HELP[employee.mcpStatus]}>
            {employee.mcpStatus}
          </HelpBadge>
          <HelpBadge bg={employee.ontologyConfigured ? 'success' : 'secondary'} description={HELP_TEXT.ontology}>
            {employee.ontologyConfigured ? 'ontology on' : 'ontology off'}
          </HelpBadge>
        </div>
        {selectedMcpServers.length ? (
          <div className="text-muted small">selected: {selectedMcpServers.join(', ')}</div>
        ) : null}
        {observedMcpServers.length ? (
          <div className="text-muted small">observed: {observedMcpServers.join(', ')}</div>
        ) : null}
      </td>
      <td>
        <HelpBadge bg={setupVariant(employee.setupStatus)} description={ROLLOUT_SETUP_HELP[employee.setupStatus]}>
          {employee.setupStatus}
        </HelpBadge>
      </td>
      <td>
        <HelpBadge bg={updateVariant(employee.updateStatus)} description={ROLLOUT_UPDATE_HELP[employee.updateStatus]}>
          {employee.updateStatus}
        </HelpBadge>
        {employee.lastError ? <div className="text-danger small">{employee.lastError}</div> : null}
        {employee.lastErrorAt ? <div className="text-muted small">{formatDate(employee.lastErrorAt)}</div> : null}
      </td>
      <td className="text-end">{employee.sessions.toLocaleString()}</td>
      <td className="text-end">{employee.events.toLocaleString()}</td>
      <td className="text-muted small">{formatDate(employee.lastSeenAt)}</td>
    </tr>
  );
}

export default AgentRolloutHealthPanel;
